import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { NHANVIEN } from '../constants';
import { Get, Post, Put, DeleteSingle } from '../../utils/httpProvider';
import {
  NHANVIEN_API,
  NGUOIDUNG_API,
  BOPHAN_API,
  TEAM_API
} from '../../utils/apiUrl';

export const nhanVienGetAllAction = createAsyncThunk(
  NHANVIEN.GET_ALL,
  async (_, { rejectWithValue }) => {
    try {
      const data = await Get(NHANVIEN_API.BASE, null, null);
      if (!data) return rejectWithValue('Không lấy được danh sách nhân viên');
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const nhanVienGetByIdAction = createAsyncThunk(
  NHANVIEN.GET_BY_ID,
  async (id, { rejectWithValue }) => {
    try {
      const data = await Get(NHANVIEN_API.BY_ID, id, null);
      if (!data) return rejectWithValue('Không tìm thấy nhân viên');
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const nhanVienCreateAction = createAsyncThunk(
  NHANVIEN.CREATE,
  async (model, { rejectWithValue }) => {
    try {
      const res = await Post(NHANVIEN_API.CREATE, null, model, false);

      if (res && typeof res.status === 'number') {
        if (res.status === 200 || res.status === 201) {
          let body = null;
          try {
            body = await res.json(); 
          } catch (e) {
            // body rỗng thì thôi
          }
          // nếu API không trả gì, dùng model tối thiểu để đẩy vào list
          return body || model;
        }

        let errBody = null;
        try {
          errBody = await res.json();
        } catch (e) {
          // không đọc được body thì dùng message chung
        }
        return rejectWithValue(errBody || 'Thêm nhân viên thất bại');
      }

      if (!res) return rejectWithValue('Thêm nhân viên thất bại');
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const nhanVienUpdateAction = createAsyncThunk(
  NHANVIEN.UPDATE,
  async ({ id, data, viewData }, { rejectWithValue }) => {
    try {
      const res = await Put(NHANVIEN_API.UPDATE, id, data);

      if (res && typeof res.status === 'number') {
        if (res.status >= 200 && res.status < 300) {
          return { id, ...data, ...(viewData || {}) };
        }

        let errBody = null;
        try {
          errBody = await res.json();
        } catch (e) {
          // body rỗng hoặc stream đã đọc thì bỏ qua
        }
        return rejectWithValue(errBody || 'Cập nhật nhân viên thất bại');
      }

      if (!res) {
        return rejectWithValue('Cập nhật nhân viên thất bại');
      }

      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const nhanVienDeleteAction = createAsyncThunk(
  NHANVIEN.DELETE,
  async (id, { rejectWithValue }) => {
    try {
      const res = await DeleteSingle(NHANVIEN_API.DELETE, id, null);

      if (res && typeof res.status === 'number') {
        if (res.status === 200 || res.status === 204) {
          return id;
        }

        let errBody = null;
        try {
          errBody = await res.json();
        } catch (e) {
          // không đọc được body thì dùng message chung
        }
        return rejectWithValue(errBody || 'Xoá nhân viên thất bại');
      }

      if (!res) return rejectWithValue('Xoá nhân viên thất bại');
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const nhanVienLoadFormDataAction = createAsyncThunk(
  NHANVIEN.LOAD_AVAILABLE_NGUOIDUNG,
  async (_, { rejectWithValue }) => {
    try {
      const [nguoiDungs, boPhans, teams] = await Promise.all([
        Get(NGUOIDUNG_API.GET_AVAILABLE_FOR_NHANVIEN, null, null),
        Get(BOPHAN_API.BASE, null, null),
        Get(TEAM_API.BASE, null, null)
      ]);

      return {
        nguoiDungs: nguoiDungs || [],
        boPhans: boPhans || [],
        teams: teams || []
      };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const nguoiDungSyncFromAccountAction = createAsyncThunk(
  NHANVIEN.SYNC_FROM_ACCOUNT,
  async (model, { rejectWithValue }) => {
    try {
      const res = await Post(
        NGUOIDUNG_API.SYNC_FROM_ACCOUNT,
        null,
        model,
        false
      );

      if (res && typeof res.status === 'number') {
        if (res.status === 200 || res.status === 204) {
          return true;
        }

        let errBody = null;
        try {
          errBody = await res.json();
        } catch (e) {
          // không đọc được body thì dùng message chung
        }
        return rejectWithValue(errBody || 'Đồng bộ Người dùng thất bại');
      }

      if (!res) return rejectWithValue('Đồng bộ Người dùng thất bại');
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const initialState = {
  list: [],
  item: null,
  nguoiDungs: [],
  boPhans: [],
  teams: [],
  loading: false,
  error: null
};

export const nhanVienSlice = createSlice({
  name: NHANVIEN.NAME_SLICE,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(nhanVienGetAllAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(nhanVienGetAllAction.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(nhanVienGetAllAction.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          (action.error && action.error.message) ||
          'Lỗi khi tải danh sách nhân viên';
      })

      .addCase(nhanVienGetByIdAction.fulfilled, (state, action) => {
        state.item = action.payload || null;
      })

      .addCase(nhanVienCreateAction.fulfilled, (state, action) => {
        if (action.payload) state.list.push(action.payload);
      })
      .addCase(nhanVienCreateAction.rejected, (state, action) => {
        state.error =
          action.payload ||
          (action.error && action.error.message) ||
          'Thêm nhân viên thất bại';
      })

      .addCase(nhanVienUpdateAction.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated) return;
        const idx = state.list.findIndex((x) => x.id === updated.id);
        if (idx >= 0) {
          state.list[idx] = { ...state.list[idx], ...updated };
        }
        if (state.item && state.item.id === updated.id) {
          state.item = { ...state.item, ...updated };
        }
      })
      .addCase(nhanVienUpdateAction.rejected, (state, action) => {
        state.error =
          action.payload ||
          (action.error && action.error.message) ||
          'Cập nhật nhân viên thất bại';
      })

      .addCase(nhanVienDeleteAction.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((x) => x.id !== id);
      })
      .addCase(nhanVienDeleteAction.rejected, (state, action) => {
        state.error =
          action.payload ||
          (action.error && action.error.message) ||
          'Xoá nhân viên thất bại';
      })

      .addCase(nhanVienLoadFormDataAction.fulfilled, (state, action) => {
        state.nguoiDungs = action.payload?.nguoiDungs || [];
        state.boPhans = action.payload?.boPhans || [];
        state.teams = action.payload?.teams || [];
      })
      .addCase(nhanVienLoadFormDataAction.rejected, (state, action) => {
        state.error =
          action.payload ||
          (action.error && action.error.message) ||
          'Không tải được dữ liệu form nhân viên';
      })

      .addCase(nguoiDungSyncFromAccountAction.rejected, (state, action) => {
        state.error =
          action.payload ||
          (action.error && action.error.message) ||
          'Đồng bộ Người dùng thất bại';
      });
  }
});

export default nhanVienSlice.reducer;
